<script setup>
import { $api } from '@/utils/api'
import { computed, onMounted, ref } from 'vue'
import VueCal from 'vue-cal'
import 'vue-cal/dist/vuecal.css'

definePage({
  meta: {
    layout: 'default',
    requiresAuth: true,
    roles: ['secretary_anesthetist'],
  },
})

const loading = ref(false)
const error = ref('')
const secretaryAnaesthetists = ref([])
const selectedAnaesthetistIds = ref([])
const selectedAnaesthetist = ref(null)
const detailsDialog = ref(false)
const filtersDrawer = ref(false)
const calendarFullscreen = ref(false)
const currentView = ref('week')
const selectedDate = ref(new Date())

const palette = [
  '#2563eb',
  '#0f766e',
  '#7c3aed',
  '#be123c',
  '#b45309',
  '#0369a1',
  '#15803d',
  '#4338ca',
]

const dayLabel = day => ({
  0: 'Sun',
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
}[day] ?? 'Day')

const formatTime = value => {
  if (!value)
    return '—'

  return String(value).slice(0, 5)
}

const normalizeTime = value => {
  const stringValue = String(value ?? '').trim()

  if (!stringValue)
    return '00:00:00'

  if (/^\d{2}:\d{2}$/.test(stringValue))
    return `${stringValue}:00`

  return stringValue.slice(0, 8)
}

const timeToMinutes = value => {
  const [hours = '0', minutes = '0'] = normalizeTime(value).split(':')

  return (Number(hours) * 60) + Number(minutes)
}

const mergeIntervals = intervals => {
  const sorted = [...intervals].sort((first, second) => first.start - second.start)
  const merged = []

  for (const interval of sorted) {
    const lastInterval = merged[merged.length - 1]

    if (!lastInterval || interval.start > lastInterval.end) {
      merged.push({ ...interval })
      continue
    }

    lastInterval.end = Math.max(lastInterval.end, interval.end)
  }

  return merged
}

const selectedAnaesthetists = computed(() => {
  if (!selectedAnaesthetistIds.value.length)
    return secretaryAnaesthetists.value

  const ids = new Set(selectedAnaesthetistIds.value.map(id => Number(id)))

  return secretaryAnaesthetists.value.filter(item => ids.has(Number(item.id)))
})

const getWeekStart = date => {
  const baseDate = new Date(date)
  const day = baseDate.getDay()
  const diff = (day + 6) % 7

  baseDate.setHours(0, 0, 0, 0)
  baseDate.setDate(baseDate.getDate() - diff)

  return baseDate
}

const calendarTimeFrom = 7 * 60
const calendarTimeTo = 22 * 60

const buildDateTime = (weekStart, dayOfWeek, time) => {
  const date = new Date(weekStart)
  const normalizedDay = Number(dayOfWeek)
  const [hours = '0', minutes = '0', seconds = '0'] = normalizeTime(time).split(':')

  date.setDate(weekStart.getDate() + ((normalizedDay + 6) % 7))
  date.setHours(Number(hours), Number(minutes), Number(seconds), 0)

  return date
}

const buildDateTimeFromMinutes = (weekStart, dayOfWeek, minutes) => {
  const date = new Date(weekStart)
  const normalizedDay = Number(dayOfWeek)
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60

  date.setDate(weekStart.getDate() + ((normalizedDay + 6) % 7))
  date.setHours(hours, mins, 0, 0)

  return date
}

const hexToRgb = color => {
  const sanitized = String(color ?? '').replace('#', '').trim()

  if (!/^[\da-f]{6}$/iu.test(sanitized))
    return null

  return {
    red: Number.parseInt(sanitized.slice(0, 2), 16),
    green: Number.parseInt(sanitized.slice(2, 4), 16),
    blue: Number.parseInt(sanitized.slice(4, 6), 16),
  }
}

const withAlpha = (color, alphaPercent) => {
  const rgb = hexToRgb(color)

  if (!rgb)
    return color

  return `rgb(${rgb.red} ${rgb.green} ${rgb.blue} / ${alphaPercent}%)`
}

const vueCalThemeStyle = {
  '--vuecal-primary-color': '#82afc9',
  '--vuecal-secondary-color': '#020617',
  '--vuecal-base-color': '#e2e8f0',
  '--vuecal-contrast-color': '#020617',
  '--vuecal-border-color': 'rgb(148 163 184 / 10%)',
  '--vuecal-header-color': '#cbd5e1',
  '--vuecal-event-color': '#e2e8f0',
  '--vuecal-event-border-color': 'rgb(255 255 255 / 8%)',
  '--vuecal-border-radius': '10px',
}

const anaesthetistSplits = computed(() => selectedAnaesthetists.value.map((anaesthetist, anaesthetistIndex) => ({
  id: Number(anaesthetist.id),
  label: anaesthetist.full_name,
  class: `anaesthetist-split anaesthetist-split--${anaesthetist.id}`,
  color: anaesthetist.avatar_color || palette[anaesthetistIndex % palette.length],
})))

const calendarEvents = computed(() => {
  const weekStart = getWeekStart(selectedDate.value)

  return selectedAnaesthetists.value.flatMap((anaesthetist, anaesthetistIndex) => {
    const color = anaesthetist.avatar_color || palette[anaesthetistIndex % palette.length]

    const rulesByDay = new Map()

    ;(anaesthetist.availability_rules ?? []).forEach(rule => {
      const day = Number(rule.day_of_week)
      const intervals = rulesByDay.get(day) ?? []

      intervals.push({
        start: Math.max(calendarTimeFrom, timeToMinutes(rule.start_time)),
        end: Math.min(calendarTimeTo, timeToMinutes(rule.end_time)),
        rule,
      })

      rulesByDay.set(day, intervals)
    })

    const events = []

    for (let day = 0; day <= 6; day += 1) {
      const mergedIntervals = mergeIntervals((rulesByDay.get(day) ?? [])
        .filter(interval => interval.end > interval.start)
        .map(interval => ({ ...interval })))

      let cursor = calendarTimeFrom

      if (!mergedIntervals.length) {
        events.push({
          title: 'Unavailable',
          start: buildDateTimeFromMinutes(weekStart, day, calendarTimeFrom),
          end: buildDateTimeFromMinutes(weekStart, day, calendarTimeTo),
          class: 'unavailability-event',
          split: Number(anaesthetist.id),
          background: true,
          kind: 'unavailable',
          anaesthetist,
          style: 'background: repeating-linear-gradient(-45deg, rgb(15 23 42 / 12%) 0 12px, rgb(30 41 59 / 18%) 12px 24px); border: 1px dashed rgb(100 116 139 / 18%);',
        })
        continue
      }

      for (const interval of mergedIntervals) {
        if (interval.start > cursor) {
          events.push({
            title: 'Unavailable',
            start: buildDateTimeFromMinutes(weekStart, day, cursor),
            end: buildDateTimeFromMinutes(weekStart, day, interval.start),
            class: 'unavailability-event',
            split: Number(anaesthetist.id),
            background: true,
            kind: 'unavailable',
            anaesthetist,
            style: 'background: repeating-linear-gradient(-45deg, rgb(15 23 42 / 12%) 0 12px, rgb(30 41 59 / 18%) 12px 24px); border: 1px dashed rgb(100 116 139 / 18%);',
          })
        }

        events.push({
          start: buildDateTime(weekStart, day, interval.rule.start_time),
          end: buildDateTime(weekStart, day, interval.rule.end_time),
          title: anaesthetist.full_name,
          content: `${formatTime(interval.rule.start_time)}-${formatTime(interval.rule.end_time)}`,
          class: 'availability-event',
          split: Number(anaesthetist.id),
          background: false,
          kind: 'availability',
          style: `background: linear-gradient(180deg, ${withAlpha(color, 28)}, ${withAlpha(color, 16)}); border-color: ${withAlpha(color, 42)}; color: ${color}; box-shadow: inset 0 0 0 0.5px ${withAlpha(color, 36)};`,
          anaesthetist,
          rule: interval.rule,
        })

        cursor = Math.max(cursor, interval.end)
      }

      if (cursor < calendarTimeTo) {
        events.push({
          title: 'Unavailable',
          start: buildDateTimeFromMinutes(weekStart, day, cursor),
          end: buildDateTimeFromMinutes(weekStart, day, calendarTimeTo),
          class: 'unavailability-event',
          split: Number(anaesthetist.id),
          background: true,
          kind: 'unavailable',
          anaesthetist,
          style: 'background: repeating-linear-gradient(-45deg, rgb(15 23 42 / 12%) 0 12px, rgb(30 41 59 / 18%) 12px 24px); border: 1px dashed rgb(100 116 139 / 18%);',
        })
      }
    }

    return events
  })
})

const loadCalendar = async () => {
  loading.value = true
  error.value = ''

  try {
    const response = await $api('/api/secretary-anesthetists/calendar', {
      method: 'GET',
    })

    secretaryAnaesthetists.value = response?.data?.data ?? response?.data ?? []
    selectedAnaesthetistIds.value = secretaryAnaesthetists.value.map(item => item.id)
  }
  catch (err) {
    error.value = err?.response?._data?.message
      ?? err?.data?.message
      ?? err?.message
      ?? 'Unable to load the availability calendar.'
    secretaryAnaesthetists.value = []
  }
  finally {
    loading.value = false
  }
}

const handleEventClick = event => {
  if (event?.kind !== 'availability')
    return

  selectedAnaesthetist.value = event?.anaesthetist ?? null
  detailsDialog.value = Boolean(selectedAnaesthetist.value)
}

const switchView = viewName => {
  currentView.value = viewName
}

const toggleCalendarFullscreen = () => {
  calendarFullscreen.value = !calendarFullscreen.value
}

onMounted(() => {
  loadCalendar()
})
</script>

<template>
  <div
    class="secretary-calendar-page"
    :class="{ 'secretary-calendar-page--fullscreen': calendarFullscreen }"
  >
    <div class="secretary-calendar-toolbar mb-4">
      <div>
        <h2 class="text-h4 mb-2">
          Availability calendar
        </h2>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Weekly availability and unavailability of the anaesthetists assigned to your account.
        </p>
        <div class="calendar-legend mt-3">
          <span class="calendar-legend__item">
            <span class="calendar-legend__swatch calendar-legend__swatch--available" />
            Available slot
          </span>
          <span class="calendar-legend__item">
            <span class="calendar-legend__swatch calendar-legend__swatch--unavailable" />
            Unavailable period
          </span>
          <span class="calendar-legend__hint">
            Click a colored slot to open the anaesthetist details.
          </span>
        </div>
      </div>

      <div class="secretary-calendar-toolbar__actions">
        <VBtn
          prepend-icon="ri-filter-3-line"
          variant="outlined"
          @click="filtersDrawer = true"
        >
          Anaesthetists
        </VBtn>

        <VBtnToggle
          :model-value="currentView"
          density="compact"
          divided
          mandatory
        >
          <VBtn
            value="day"
            @click="switchView('day')"
          >
            Day
          </VBtn>
          <VBtn
            value="week"
            @click="switchView('week')"
          >
            Week
          </VBtn>
          <VBtn
            value="month"
            @click="switchView('month')"
          >
            Month
          </VBtn>
        </VBtnToggle>

        <VBtn
          :prepend-icon="calendarFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'"
          variant="tonal"
          @click="toggleCalendarFullscreen"
        >
          {{ calendarFullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}
        </VBtn>
      </div>
    </div>

    <VAlert
      v-if="error"
      type="error"
      class="mb-4"
    >
      {{ error }}
    </VAlert>

    <VProgressLinear
      v-if="loading"
      indeterminate
      class="mb-4"
    />

    <div class="secretary-calendar-surface">
      <div class="secretary-calendar-shell">
        <VueCal
          :active-view="currentView"
          :dark="true"
          :disable-views="['years', 'year']"
          :editable-events="false"
          :events="calendarEvents"
          :selected-date="selectedDate"
          :split-days="anaesthetistSplits"
          :style="vueCalThemeStyle"
          :time-cell-height="44"
          :time-from="calendarTimeFrom"
          :time-step="30"
          :time-to="calendarTimeTo"
          events-on-month-view
          locale="en"
          today-button
          @event-click="handleEventClick"
          @update:selected-date="selectedDate = $event"
        >
          <template #split-label="{ split }">
            <div
              class="anaesthetist-split-label"
              :style="{ '--split-accent': split.color }"
            >
              <VAvatar
                size="28"
                :color="split.color"
              >
                {{ split.label?.charAt(0) || 'N' }}
              </VAvatar>
              <div>
                <div class="text-body-2 font-weight-medium">
                  {{ split.label }}
                </div>
                <div class="text-caption text-medium-emphasis">
                  Resource view
                </div>
              </div>
            </div>
          </template>

          <template #event="{ event }">
            <button
              v-if="event.kind === 'availability'"
              class="availability-event-card"
              type="button"
              @click.stop="handleEventClick(event)"
            >
              <span class="availability-event-card__title">{{ event.title }}</span>
              <span class="availability-event-card__time">{{ event.content }}</span>
            </button>
          </template>
        </VueCal>
      </div>
    </div>

    <VNavigationDrawer
      v-model="filtersDrawer"
      location="right"
      temporary
      width="380"
    >
      <div class="filters-drawer">
        <div class="filters-drawer__header">
          <div>
            <div class="text-h6">
              Visible anaesthetists
            </div>
            <div class="text-body-2 text-medium-emphasis">
              Choose which resources are shown in the calendar.
            </div>
          </div>

          <VBtn
            icon="ri-close-line"
            variant="text"
            @click="filtersDrawer = false"
          />
        </div>

        <VAutocomplete
          v-model="selectedAnaesthetistIds"
          :items="secretaryAnaesthetists"
          class="mb-4"
          item-title="full_name"
          item-value="id"
          chips
          closable-chips
          clearable
          multiple
          label="Visible anaesthetists"
          prepend-inner-icon="ri-user-search-line"
          variant="outlined"
        />

        <VList density="compact">
          <VListItem
            v-for="anaesthetist in secretaryAnaesthetists"
            :key="anaesthetist.id"
            @click="selectedAnaesthetist = anaesthetist; detailsDialog = true"
          >
            <template #prepend>
              <VAvatar
                size="30"
                :color="anaesthetist.avatar_color || palette[Number(anaesthetist.id) % palette.length]"
              >
                {{ anaesthetist.full_name?.charAt(0) || 'N' }}
              </VAvatar>
            </template>

            <VListItemTitle class="text-wrap">
              {{ anaesthetist.full_name }}
            </VListItemTitle>
            <VListItemSubtitle>
              {{ anaesthetist.availability_count }} slots
            </VListItemSubtitle>
          </VListItem>
        </VList>
      </div>
    </VNavigationDrawer>

    <VDialog
      v-model="detailsDialog"
      max-width="720"
    >
      <VCard v-if="selectedAnaesthetist">
        <VCardTitle class="d-flex justify-space-between align-center">
          <span>Anaesthetist availability</span>
          <VBtn
            icon="ri-close-line"
            variant="text"
            @click="detailsDialog = false"
          />
        </VCardTitle>
        <VCardText>
          <div class="d-flex flex-column ga-3">
            <div>
              <div class="text-caption text-medium-emphasis">
                Name
              </div>
              <div class="text-body-1 font-weight-medium">
                {{ selectedAnaesthetist.full_name }}
              </div>
            </div>
            <div>
              <div class="text-caption text-medium-emphasis">
                GMC
              </div>
              <div class="text-body-1 font-weight-medium">
                {{ selectedAnaesthetist.gmc_number || '—' }}
              </div>
            </div>
            <div>
              <div class="text-caption text-medium-emphasis">
                Email
              </div>
              <div class="text-body-1 font-weight-medium">
                {{ selectedAnaesthetist.primary_email || '—' }}
              </div>
            </div>
            <div>
              <div class="text-caption text-medium-emphasis">
                Hospitals
              </div>
              <div class="text-body-1 font-weight-medium">
                {{ selectedAnaesthetist.hospitals?.map(item => item.name).join(', ') || '—' }}
              </div>
            </div>
            <div>
              <div class="text-caption text-medium-emphasis">
                Specialist areas
              </div>
              <div class="text-body-1 font-weight-medium">
                {{ selectedAnaesthetist.specialist_areas?.map(item => item.name).join(', ') || '—' }}
              </div>
            </div>
            <div>
              <div class="text-caption text-medium-emphasis mb-2">
                Weekly availability
              </div>
              <div class="d-flex flex-wrap ga-2">
                <VChip
                  v-for="rule in selectedAnaesthetist.availability_rules"
                  :key="rule.id"
                  variant="tonal"
                  color="primary"
                >
                  {{ rule.label }}
                </VChip>
              </div>
            </div>
            <div>
              <div class="text-caption text-medium-emphasis mb-2">
                Weekly coverage
              </div>
              <div class="d-flex flex-wrap ga-2">
                <VChip
                  v-for="day in [0, 1, 2, 3, 4, 5, 6]"
                  :key="day"
                  color="grey"
                  variant="tonal"
                >
                  {{ dayLabel(day) }} {{ selectedAnaesthetist.availability_rules?.some(rule => Number(rule.day_of_week) === day) ? 'covered' : 'off' }}
                </VChip>
              </div>
            </div>
          </div>
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>

<style>
.secretary-calendar-page {
  display: flex;
  flex-direction: column;
  color: rgb(226 232 240);
  gap: 16px;
  min-block-size: calc(100vh - 170px);
}

.secretary-calendar-page--fullscreen {
  position: fixed;
  z-index: 2000;
  padding: 20px;
  background: rgb(2 6 23 / 98%);
  inset: 0;
}

.secretary-calendar-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.secretary-calendar-toolbar__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
}

.secretary-calendar-surface {
  display: flex;
  overflow: hidden;
  flex: 1 1 auto;
  border: 1px solid rgb(148 163 184 / 14%);
  border-radius: 20px;
  background: linear-gradient(180deg, rgb(15 23 42 / 98%), rgb(2 6 23 / 100%));
  min-block-size: 0;
}

.secretary-calendar-shell .vuecal {
  background: transparent;
  block-size: 100%;
  color: rgb(226 232 240);
  inline-size: 100%;
}

.secretary-calendar-shell {
  flex: 1 1 auto;
  min-block-size: 0;
}

.secretary-calendar-shell .vuecal__title-bar {
  border-block-end: 1px solid rgb(148 163 184 / 10%);
  padding-block: 0 16px;
  padding-inline: 0;
}

.secretary-calendar-shell .vuecal__split-days-headers {
  background: rgb(15 23 42 / 90%);
}

.secretary-calendar-shell .vuecal__cell--has-splits {
  background: rgb(2 6 23 / 22%);
}

.secretary-calendar-shell .vuecal__bg,
.secretary-calendar-shell .vuecal__body,
.secretary-calendar-shell .vuecal__weekdays-headings,
.secretary-calendar-shell .vuecal__weekdays-headings div,
.secretary-calendar-shell .vuecal__time-column {
  background: transparent;
}

.secretary-calendar-shell .vuecal__time-column,
.secretary-calendar-shell .vuecal__weekdays-headings,
.secretary-calendar-shell .vuecal__cells,
.secretary-calendar-shell .vuecal__cell,
.secretary-calendar-shell .vuecal__split-days-headers,
.secretary-calendar-shell .vuecal__split-days-headers div {
  border-color: rgb(148 163 184 / 10%) !important;
}

.secretary-calendar-shell .vuecal__time-cell-label,
.secretary-calendar-shell .vuecal__weekday-name,
.secretary-calendar-shell .vuecal__title {
  color: rgb(203 213 225);
}

.secretary-calendar-shell .vuecal__event {
  padding: 0;
  border-radius: 12px;
  box-shadow: none;
}

.secretary-calendar-shell .vuecal__event.unavailability-event {
  border-radius: 0;
  box-shadow: none;
  opacity: 0.72;
}

.secretary-calendar-shell .vuecal__event-title,
.secretary-calendar-shell .vuecal__event-time {
  display: none;
}

.anaesthetist-split-label {
  display: flex;
  align-items: center;
  border-radius: 10px;
  background: linear-gradient(90deg, rgb(255 255 255 / 3%), transparent);
  border-inline-start: 2px solid var(--split-accent);
  gap: 12px;
  padding-block: 8px;
  padding-inline: 10px 6px;
}

.availability-event-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border: 0.5px solid rgb(255 255 255 / 7%);
  border-radius: 12px;
  background: inherit;
  cursor: pointer;
  gap: 2px;
  inline-size: 100%;
  padding-block: 10px;
  padding-inline: 12px;
  text-align: start;
}

.availability-event-card:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
}

.availability-event-card__title {
  font-size: 0.85rem;
  font-weight: 700;
}

.availability-event-card__time {
  font-size: 0.72rem;
  opacity: 0.85;
}

.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 18px;
}

.calendar-legend__item {
  display: inline-flex;
  align-items: center;
  color: rgb(203 213 225);
  font-size: 0.85rem;
  gap: 8px;
}

.calendar-legend__hint {
  color: rgb(148 163 184);
  font-size: 0.85rem;
  opacity: 0.72;
}

.calendar-legend__swatch {
  display: inline-block;
  border-radius: 6px;
  block-size: 18px;
  inline-size: 18px;
}

.calendar-legend__swatch--available {
  border: 1px solid rgb(96 165 250 / 30%);
  background: linear-gradient(135deg, rgb(59 130 246 / 24%), rgb(14 165 233 / 12%));
}

.calendar-legend__swatch--unavailable {
  border: 1px dashed rgb(148 163 184 / 22%);
  background: repeating-linear-gradient(-45deg, rgb(15 23 42 / 14%) 0 10px, rgb(51 65 85 / 18%) 10px 20px);
}

.filters-drawer {
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: rgb(2 6 23 / 98%);
  block-size: 100%;
  border-inline-start: 1px solid rgb(148 163 184 / 12%);
}

.filters-drawer__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-block-end: 20px;
}

@media (max-width: 960px) {
  .secretary-calendar-page {
    min-block-size: calc(100vh - 120px);
  }

  .secretary-calendar-toolbar {
    flex-direction: column;
  }

  .secretary-calendar-toolbar__actions {
    justify-content: flex-start;
    inline-size: 100%;
  }

  .secretary-calendar-shell .vuecal__split-days-headers {
    font-size: 0.75rem;
  }

  .calendar-legend {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
